import React, { useState, useMemo, useEffect } from "react";
import { MdEdit, MdDelete, MdFilterList } from "react-icons/md";
import PageLayout from "../../Layouts/PageLayout/PageLayout";
import Table from "../../components/common/Table/Table";
import Button from "../../components/common/Button/Button";
import InputSearch from "../../components/common/Inputs/InputSearch/InputSearch";
import { FiDownload } from "react-icons/fi";
import { GrAdd } from "react-icons/gr";
import { LuArrowDownUp } from "react-icons/lu";
import "./ObrasMenores.scss";
import { getApiInstance } from "../../utils/api";
import { CiCircleCheck, CiClock1 } from "react-icons/ci";
import { CgDanger } from "react-icons/cg";
import { useZone } from "../../context/ZoneContext";
import { mapList } from "../../utils/utils";

const columnasObrasMenores = [
  { key: "codObraMenor", label: "ID", visible: false },
  {
    key: "nombre",
    label: "Código Centro",
    visible: true,
    sortable: true,
    isLink: true,
    width: "130px",
  },
  {
    key: "localidad",
    label: "Localidad",
    visible: true,
    sortable: true,
    width: "200px",
  },
  {
    key: "fechaFin",
    label: "Fecha fin",
    visible: true,
    sortable: true,
    type: "date",
    width: "120px",
  },
  {
    key: "intermediario",
    label: "Empresa Intermediaria",
    visible: true,
    sortable: true,
    width: "180px",
  },
  {
    key: "cadena",
    label: "Cadena",
    visible: true,
    sortable: true,
    width: "120px",
  },
  {
    key: "codEstado",
    label: "Estado",
    visible: true,
    sortable: true,
    width: "120px",
  },
];

const estadosBadges = [
  { label: "Sin datos", type: "info", Icon: CiClock1 },
  { label: "Pendiente", type: "info", Icon: CiClock1 },
  { label: "Incidencia", type: "warning", Icon: CgDanger },
  { label: "Validada", type: "success", Icon: CiCircleCheck },
  { label: "Cerrada", type: "primary", Icon: CiCircleCheck },
];

const filterFields = [
  { key: "nombre", label: "Código Centro", type: "multiselect" },
  { key: "localidad", label: "Localidad", type: "multiselect" },
  { key: "intermediario", label: "Empresa Intermediaria", type: "multiselect" },
  { key: "fechaFin", label: "Fecha fin", type: "date" },
  { key: "cadena", label: "Cadena", type: "multiselect" },
  {
    key: "codEstado",
    label: "Estado",
    type: "cards",
    labelMap: estadosBadges.map((item) => item.label),
  },
];

export default function ObrasMenores() {
  const [obrasMenores, setObrasMenores] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { selectedZone } = useZone();
  const [productores, setProductores] = useState([]);
  const [comunidades, setComunidades] = useState([]);
  const [paises, setPaises] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [usuariosIntermediarios, setUsuariosIntermediarios] = useState([]);
  const [cadenas, setCadenas] = useState([]);

  const fetchObrasMenores = async () => {
    try {
      const api = getApiInstance();
      const response = await api.get(
        `/api/ObraMenor/GetObrasMenoresFromUser/${selectedZone.value}`
      );

      setObrasMenores(response.data);
      setError(null);
    } catch (err) {
      setError(
        "No se han podido cargar las obras. Vuelve a intentarlo más tarde."
      );
      console.error(err);
    } finally {
    }
  };

  const fetchUsuariosIntermediarios = async () => {
    const api = getApiInstance();
    try {
      let usuariosIntermediariosData = [];

      const response = await api.get(
        `/api/Usuario/GetAllUsuariosIntermediarios/${selectedZone.value}`
      );
      usuariosIntermediariosData = response.data;

      setUsuariosIntermediarios(
        mapList(
          usuariosIntermediariosData,
          ["nombre", "apellidos"],
          "codUsuario",
          "codIntermediario",
          "codIntermediario"
        )
      );
      setError(null);
    } catch (err) {
      setError(
        "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCadenas = async () => {
    const api = getApiInstance();
    try {
      let cadenasData = [];

      const response = await api.get(`/api/Cadenas/GetAllCadenas`);
      cadenasData = response.data;

      setCadenas([...mapList(cadenasData, "cadena", "codCadena", "codCadena")]);
      setError(null);
    } catch (err) {
      setError(
        "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
      );
      console.error(err);
    } finally {
    }
  };

  const fetchComunidades = async () => {
    const api = getApiInstance();

    try {
      let comunidadesData = [];

      const response = await api.get(
        `/api/Comunidades/GetComunidadesByZona/${selectedZone.value}`
      );
      comunidadesData = response.data;

      setComunidades(
        mapList(
          comunidadesData,
          "comunidad",
          "codComunidad",
          "codComunidad",
          "codPais"
        )
      );

      setError(null);
    } catch (err) {
      setError(
        "No se han podido cargar las comunidades. Vuelve a intentarlo más tarde."
      );
      console.error(err);
    } finally {
    }
  };

  const fetchPaises = async () => {
    const api = getApiInstance();

    try {
      let paisesData = [];

      const response = await api.get(
        `/api/Paises/GetPaisesByZona/${selectedZone.value}`
      );
      paisesData = response.data;

      setPaises(mapList(paisesData, "pais", "codPais", "codPais"));
      setError(null);
    } catch (err) {
      setError(
        "No se han podido cargar los países. Vuelve a intentarlo más tarde."
      );
      console.error(err);
    } finally {
    }
  };

  const fetchProvincias = async () => {
    const api = getApiInstance();
    try {
      let provinciasData = [];

      const response = await api.get(
        `/api/Provincias/GetProvinciasByZona/${selectedZone.value}`
      );
      provinciasData = response.data;

      setProvincias(
        mapList(
          provinciasData,
          "provincia",
          "codProvincia",
          "codProvincia",
          "codComunidad"
        )
      );
      setError(null);
    } catch (err) {
      setError(
        "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
      );
      console.error(err);
    } finally {
    }
  };

  const fetchProductores = async () => {
    const api = getApiInstance();
    try {
      let productoresData = [];

      const response = await api.get(
        `/api/Productores/GetProductoresByZona/${selectedZone.value}`
      );
      productoresData = response.data;

      setProductores([...mapList(productoresData, "nombre", "codProductor")]);
      setError(null);
    } catch (err) {
      setError(
        "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
      );
      console.error(err);
    } finally {
    }
  };

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);

    const loadAll = async () => {
      try {
        await fetchObrasMenores();

        await Promise.all([
          fetchUsuariosIntermediarios(),
          fetchCadenas(),
          fetchPaises(),
          fetchComunidades(),
          fetchProvincias(),
          fetchProductores(),
        ]);
      } catch (err) {
        if (isMounted) {
          setError(
            "No se han podido cargar los datos. Vuelve a intentarlo más tarde."
          );
        }
        console.error("Error en loadAll:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadAll();

    return () => {
      isMounted = false;
    };
  }, [selectedZone]);

  return (
    <PageLayout
      title="Obras Menores"
      subtitle="Aquí verás todas las Obras Menores de tu organización."
      actions={[
        <Button
          key="export"
          componentType="link"
          variant="outlined"
          href="/"
          startIcon={<FiDownload />}
        >
          Exportar
        </Button>,
        <Button
          key="import"
          componentType="link"
          variant="outlined"
          href="/"
          startIcon={<FiDownload />}
        >
          Importar
        </Button>,
      ]}
    >
      <div className="obras-menores-table">
        <Table
          columns={columnasObrasMenores}
          dataTable={obrasMenores}
          showCheckboxes={true}
          noResultsMessage="No se han encontrado obras menores."
          actions={["Eliminar", "Editar"]}
          searchTerms={[
            "nombre",
            "localidad",
            "intermediario",
            "cadena",
            "estado",
          ]}
          keyProp="codObraMenor"
          states={estadosBadges}
          stateProp="codEstado"
          elementName="Obra Menor"
          sortProp="nombre"
          filterFields={filterFields}
          loading={loading}
          formElements={[
            {
              title: "Información General",
              titleVisible: true,
              elements: [
                {
                  name: "codProductor",
                  type: "select",
                  label: "Productor",
                  placeholder: "Seleccione el productor",
                  required: false,
                  disabled: false,
                  labelVisible: true,
                  visible: true,
                  options: productores,
                },
                {
                  name: "nombre",
                  type: "text",
                  label: "Código de obra",
                  placeholder: "Ingrese el código de obra",
                  required: true,
                  disabled: false,
                  labelVisible: true,
                  visible: true,
                },
                {
                  name: "fechaInicio",
                  type: "date",
                  label: "Fecha inicio",
                  placeholder: "Seleccione la fecha de inicio",
                  required: true,
                  disabled: false,
                  labelVisible: true,
                  visible: true,
                },
                {
                  name: "fechaFin",
                  type: "date",
                  label: "Fecha fin",
                  placeholder: "Seleccione la fecha fin",
                  required: true,
                  disabled: false,
                  labelVisible: true,
                  visible: true,
                },
                {
                  name: "CodUsuario",
                  type: "select",
                  label: "Intermediario",
                  placeholder: "Seleccione el intermediario",
                  required: false,
                  disabled: false,
                  labelVisible: true,
                  visible: true,
                  options: usuariosIntermediarios,
                },
                {
                  name: "codCadena",
                  type: "select",
                  label: "Cadena",
                  placeholder: "Seleccione la cadena",
                  required: true,
                  disabled: false,
                  labelVisible: true,
                  visible: true,
                  options: cadenas,
                },
                {
                  name: "obs",
                  type: "textarea",
                  label: "Observaciones",
                  placeholder: "Ingrese observciones de la obra",
                  required: false,
                  disabled: false,
                  labelVisible: true,
                  visible: true,
                },
              ],
            },
            {
              title: "Ubicación",
              titleVisible: true,
              elements: [
                {
                  name: "direccion",
                  type: "text",
                  label: "Dirección",
                  placeholder: "Ingrese la dirección",
                  required: true,
                  disabled: false,
                  labelVisible: true,
                  visible: true,
                },
                {
                  name: "codPais",
                  type: "select",
                  label: "País",
                  placeholder: "Introduce el país",
                  options: paises,
                  required: true,
                  disabled: false,
                  labelVisible: true,
                  visible: true,
                },
                {
                  name: "codComunidad",
                  type: "select",
                  label: "Comunidad",
                  placeholder: "Introduce la comunidad",
                  options: comunidades,
                  required: true,
                  disabled: false,
                  labelVisible: true,
                  visible: true,
                  associatedFormElement: "codPais",
                },
                {
                  name: "codProvincia",
                  type: "select",
                  label: "Provincia",
                  placeholder: "Introduce la provincia",
                  options: provincias,
                  required: true,
                  disabled: false,
                  labelVisible: true,
                  visible: true,
                  associatedFormElement: "codComunidad",
                },
                {
                  name: "localidad",
                  type: "text",
                  label: "Localidad",
                  placeholder: "Ingrese el nombre de la localidad",
                  required: true,
                  disabled: false,
                  labelVisible: true,
                  visible: true,
                },
                {
                  name: "codZona",
                  type: "number",
                  label: "Zona",
                  visible: false,
                  defaultValue: "1",
                },
              ],
            },
            {
              title: "Cantidades generadas",
              titleVisible: true,
              elements: [
                {
                  name: "cantidadRP",
                  type: "number",
                  label: "Generado residuos peligrosos (KG)",
                  placeholder: "Ingrese la cantidad",
                  required: true,
                  disabled: false,
                  labelVisible: true,
                  visible: true,
                },
                {
                  name: "cantidadRNP",
                  type: "number",
                  label: "Generado residuos no peligrosos (KG)",
                  placeholder: "Ingrese la cantidad",
                  required: true,
                  disabled: false,
                  labelVisible: true,
                  visible: true,
                },
              ],
            },
          ]}
          addApiUrl = "/api/ObraMenor/SetObraMenor"
          editApiUrl = "/api/ObraMenor/UpdateObraMenor"
          deleteApiUrl = "/api/ObraMenor/DeleteObraMenor"
        />
      </div>
    </PageLayout>
  );
}